import { useCallback, useEffect, useState } from "react";

import { api, clinicHeader, readError } from "../api/client";
import { ErrorNote } from "../components/ErrorNote";

type Week = {
  week: string;
  booked: number;
  completed: number;
  cancelled: number;
  no_show: number;
};

function weekLabel(iso: string) {
  const [year, week] = iso.split("-W");
  return `Week ${Number(week)}, ${year}`;
}

function rate(row: Week) {
  const answered = row.completed + row.no_show;
  if (answered === 0) return null;
  return Math.round((row.no_show / answered) * 100);
}

/**
 * How the clinic's appointments went, by week (SCH-F-033).
 *
 * Counts, and no names. The question this answers is about the clinic, not about
 * anybody in particular, and a screen that listed the patients who missed
 * appointments would be a different thing with a different purpose.
 *
 * The percentage divides by the appointments somebody answered for, not by the
 * ones booked. A week half-marked would otherwise read as a good week, which is
 * the wrong direction for a number like this to be wrong in.
 */
export function Attendance() {
  const [rows, setRows] = useState<Week[]>([]);
  const [weeks, setWeeks] = useState(8);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: failed } = await api.GET("/portal/reports/attendance", {
      params: { header: clinicHeader(), query: { weeks } },
    });
    setLoading(false);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setError(null);
    setRows(((data?.items ?? []) as Week[]).slice().reverse());
  }, [weeks]);

  useEffect(() => {
    void load();
  }, [load]);

  const unanswered = rows.reduce((total, row) => total + row.booked, 0);

  /* The figure somebody opened this screen for.
   *
   * Across the whole period, not an average of the weekly percentages: a week
   * with four appointments would otherwise count as much as a week with
   * eighty. Divided by the appointments somebody actually answered for, the
   * same way each week is, so a half-marked period cannot read as a good one.
   */
  const answered = rows.reduce((n, row) => n + row.completed + row.no_show, 0);
  const missedAll = rows.reduce((n, row) => n + row.no_show, 0);
  const overall = answered === 0 ? null : Math.round((missedAll / answered) * 100);

  /* The tallest weekly rate, so the bars have something to be drawn against.
   * A fixed 100% scale would flatten every real clinic into nothing. */
  const peak = rows.reduce((high, row) => Math.max(high, rate(row) ?? 0), 0);

  return (
    <section>
      <div className="row-between">
        <h1>Attendance</h1>
        <label className="field inline">
          Looking back
          <select value={weeks} onChange={(event) => setWeeks(Number(event.target.value))}>
            {[4, 8, 13, 26, 52].map((value) => (
              <option key={value} value={value}>
                {value} weeks
              </option>
            ))}
          </select>
        </label>
      </div>

      <ErrorNote message={error} />

      {loading && rows.length === 0 && <p className="empty">Counting…</p>}

      {!loading && rows.length === 0 && (
        <p className="empty">No appointments in this period.</p>
      )}

      {rows.length > 0 && (
        <>
          {/* The answer first, then the shape of it, then the counts. This is
              the only screen in the portal that is about numbers rather than
              people, and it was drawn like every other table. */}
          <div className="att-headline">
            <span className="att-figure">
              {overall === null ? "—" : `${overall}%`}
            </span>
            <span className="att-figure-of">
              of answered appointments were missed, across {rows.length}{" "}
              {rows.length === 1 ? "week" : "weeks"}
            </span>
          </div>

          <ol className="att-bars">
            {rows.map((row) => {
              const missed = rate(row);
              const height = missed === null || peak === 0 ? 0 : (missed / peak) * 100;
              return (
                <li key={row.week} className="att-bar">
                  <span className="att-bar-track" title={weekLabel(row.week)}>
                    <span
                      className="att-bar-fill"
                      style={{ height: `${height}%` }}
                      aria-hidden="true"
                    />
                  </span>
                  <span className="att-bar-value">
                    {missed === null ? "—" : `${missed}%`}
                  </span>
                  <span className="att-bar-week">
                    {weekLabel(row.week).replace("Week ", "")}
                  </span>
                </li>
              );
            })}
          </ol>

          <div className="data-table-wrap">
          <table className="data-table records">
            <thead>
              <tr>
                <th>Week</th>
                <th>Attended</th>
                <th>Did not attend</th>
                <th>Cancelled</th>
                <th>Not yet marked</th>
                <th>Missed</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const missed = rate(row);
                return (
                  <tr key={row.week}>
                    <td>{weekLabel(row.week)}</td>
                    <td>{row.completed}</td>
                    <td>{row.no_show}</td>
                    <td>{row.cancelled}</td>
                    <td className={row.booked > 0 ? "quiet" : undefined}>{row.booked}</td>
                    <td>{missed === null ? <span className="quiet">—</span> : `${missed}%`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

          {unanswered > 0 && (
            <p className="note">
              {unanswered} {unanswered === 1 ? "appointment is" : "appointments are"} still
              unmarked, so the missed percentage leaves them out. Mark them on the
              day's list and these numbers become worth reading.
            </p>
          )}

          <p className="quiet">
            Counts only. Nobody is named here, and the patient app shows a patient
            nothing about this.
          </p>
        </>
      )}
    </section>
  );
}
